import { useMemo, useState } from "react"
import { toolsetWrites } from "../../api/mutations"
import {
	usePictograms,
	usePossibleToolsetProducts,
	useToolsetProducts,
} from "../../api/queries"
import type { GroupRights } from "../../auth/rights"
import { ActionNotes, Note } from "../../components/Note"
import { useEscape } from "../../hooks/useEscape"
import { useWriter } from "./useWriter"

const SUGGESTIONS = 15

/**
 * Состав набора (комплектация) — панель под табличной частью карточки-набора.
 *
 * У группы-набора один товар (сам набор, показан выше в «Табличной части»);
 * здесь — список товаров-компонентов, которые в него входят: их порядок и
 * раздел (пиктограмма). Это отдельный контур API (`/api/toolsets/*`,
 * `ToolsetProductsController`), не связанный с товарами карточки.
 *
 * Панель открыта на просмотр всем, кто видит карточку (RF, Lv, Rg,
 * Photographer, Translator), а на запись — только по `rights.toolsetControl`:
 * у РФ-наборов `ToolsetControllerAuthHelper` требует роль `ContentManagerRF`,
 * которой у Риги нет, значит РФ-наборы здесь только смотрят.
 */
export default function ToolsetComposition({
	groupId,
	toolsetId,
	toolsetName,
	languageId,
	rights,
}: {
	groupId: string
	toolsetId: string
	toolsetName: string
	languageId: number
	rights: GroupRights
}) {
	const rows = useToolsetProducts(groupId, toolsetId, languageId)
	const candidates = usePossibleToolsetProducts(
		groupId,
		toolsetId,
		languageId,
		rights.toolsetControl
	)
	const pictograms = usePictograms(languageId)
	const writer = useWriter(groupId)

	const [query, setQuery] = useState("")
	const [defaultPictogram, setDefaultPictogram] = useState<number | "">("")
	const [replacing, setReplacing] = useState<string | null>(null)

	const list = rows.data ?? []
	const taken = useMemo(() => new Set(list.map(row => row.productId)), [list])

	const matches = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (!needle) return []
		return (candidates.data ?? [])
			.filter(
				product =>
					!taken.has(product.id) &&
					(product.id.toLowerCase().includes(needle) ||
						product.name.toLowerCase().includes(needle))
			)
			.slice(0, SUGGESTIONS)
	}, [candidates.data, query, taken])

	const addProduct = (productId: string) => {
		setQuery("")
		void writer.run(() =>
			toolsetWrites.add(groupId, toolsetId, [
				{
					productId,
					pictogramId: defaultPictogram === "" ? null : defaultPictogram,
					sort: 0,
				},
			])
		)
	}

	const replaceProduct = (newProductId: string) => {
		const productId = replacing
		if (!productId) return
		setReplacing(null)
		setQuery("")
		if (productId === newProductId) return
		void writer.run(() =>
			toolsetWrites.replace(groupId, toolsetId, productId, newProductId)
		)
	}

	const removeProduct = (productId: string) =>
		void writer.run(() => toolsetWrites.remove(groupId, toolsetId, productId))

	const moveProduct = (productId: string, currentSort: number, targetSort: number) =>
		void writer.run(() =>
			toolsetWrites.moveSort(groupId, toolsetId, productId, currentSort, targetSort)
		)

	const changePictogram = (productId: string, pictogramId: number) =>
		void writer.run(() =>
			toolsetWrites.changePictogram(groupId, toolsetId, productId, pictogramId)
		)

	// Esc в два шага: сначала подсказки, потом выход из режима замены —
	// как в ProductPicker
	useEscape(!!replacing || query !== "", () => {
		if (query) setQuery("")
		else setReplacing(null)
	})

	return (
		<div className="tp-toolset">
			<ActionNotes action={writer} />

			{!rights.toolsetControl && (
				<Note tone="hint">
					Только просмотр: это набор российской стороны, состав правит{" "}
					<b>ContentManagerRF</b>.
				</Note>
			)}

			<div className="tp-bar">
				<span className="tp-bar__count">
					Состав набора{list.length > 0 && ` · ${list.length}`}
				</span>
				<span className="muted" title={toolsetName}>
					{toolsetName}
				</span>
				<span className="tp-bar__spacer" />
			</div>

			{rights.toolsetControl && (
				<div className="tp-picker">
					<span className="tp-picker__label">
						{replacing ? `Заменить ${replacing} на` : "Добавить"}
					</span>
					<div className="tp-picker__field">
						<input
							className="input"
							placeholder="Артикул или название"
							value={query}
							disabled={writer.busy}
							autoFocus={!!replacing}
							onChange={event => setQuery(event.target.value)}
							onKeyDown={event => {
								if (event.key !== "Enter") return
								const exact = (candidates.data ?? []).find(
									product =>
										product.id.toLowerCase() === query.trim().toLowerCase()
								)
								const pick = exact?.id ?? (matches.length === 1 ? matches[0].id : null)
								if (!pick) return
								if (replacing) replaceProduct(pick)
								else addProduct(pick)
							}}
						/>
						{matches.length > 0 && (
							<div className="tp-picker__list">
								{matches.map(product => (
									<button
										key={product.id}
										className="tp-picker__item"
										disabled={writer.busy}
										onClick={() =>
											replacing ? replaceProduct(product.id) : addProduct(product.id)
										}
									>
										<span className="mono muted">{product.id}</span>
										<span className="tp-picker__name">{product.name}</span>
									</button>
								))}
							</div>
						)}
					</div>
					{!replacing && (
						<select
							className="select"
							title="Раздел по умолчанию для добавляемого товара"
							disabled={writer.busy}
							value={defaultPictogram}
							onChange={event =>
								setDefaultPictogram(
									event.target.value === "" ? "" : Number(event.target.value)
								)
							}
						>
							<option value="">— раздел —</option>
							{(pictograms.data ?? []).map(pictogram => (
								<option key={pictogram.id} value={pictogram.id}>
									{pictogram.name}
								</option>
							))}
						</select>
					)}
					{replacing && (
						<button
							className="btn"
							title="Выйти из режима замены (Esc)"
							onClick={() => {
								setReplacing(null)
								setQuery("")
							}}
						>
							Отмена
						</button>
					)}
					<span className="muted hint-inline">
						{candidates.isLoading
							? "список товаров загружается…"
							: `доступно: ${(candidates.data ?? []).length}`}
					</span>
				</div>
			)}

			<div className="panel tp-tablewrap">
				<table className="products tp-table">
					<thead>
						<tr>
							<th className="tp-col-sort">№</th>
							<th className="tp-col-code">Код</th>
							<th className="tp-name">Наименование</th>
							<th className="tp-col-pict">Раздел</th>
							<th className="tp-col-act" />
						</tr>
					</thead>
					<tbody>
						{rows.isLoading && (
							<tr>
								<td colSpan={5}>
									<span className="spinner" />
								</td>
							</tr>
						)}

						{list.map((row, index) => (
							<tr key={row.productId}>
								<td>
									<span className="tp-sort">
										<span className="muted mono">{row.sort}</span>
										{rights.toolsetControl && list.length > 1 && (
											<span className="tp-sort__moves">
												<button
													className="tp-mini"
													title="Выше"
													disabled={index === 0 || writer.busy}
													onClick={() => moveProduct(row.productId, row.sort, index)}
												>
													↑
												</button>
												<button
													className="tp-mini"
													title="Ниже"
													disabled={index === list.length - 1 || writer.busy}
													onClick={() =>
														moveProduct(row.productId, row.sort, index + 2)
													}
												>
													↓
												</button>
											</span>
										)}
									</span>
								</td>
								<td className="mono tp-code">{row.productId}</td>
								<td className="tp-name" title={row.name}>
									{row.name}
								</td>
								<td className="tp-col-pict">
									{rights.toolsetPictograms ? (
										<select
											className="select tp-pict-select"
											disabled={writer.busy}
											title={row.pictogramName ?? undefined}
											value={row.pictogramId ?? ""}
											onChange={event => {
												const value = event.target.value
												if (value !== "") changePictogram(row.productId, Number(value))
											}}
										>
											<option value="">
												{row.pictogramId === null ? "— не выбран —" : "— убрать нельзя —"}
											</option>
											{(pictograms.data ?? []).map(pictogram => (
												<option key={pictogram.id} value={pictogram.id}>
													{pictogram.name}
												</option>
											))}
										</select>
									) : (
										<span className="muted tp-pict-value" title={row.pictogramName ?? undefined}>
											{row.pictogramName ?? "—"}
										</span>
									)}
								</td>
								<td>
									<span className="tp-actions">
										{rights.toolsetControl && (
											<>
												<button
													className="tp-mini"
													title="Заменить товар другим"
													disabled={writer.busy}
													onClick={() => {
														setReplacing(row.productId)
														setQuery("")
													}}
												>
													⇄
												</button>
												<button
													className="tp-mini tp-mini--danger"
													title="Убрать товар из набора"
													disabled={writer.busy}
													onClick={() => removeProduct(row.productId)}
												>
													✕
												</button>
											</>
										)}
									</span>
								</td>
							</tr>
						))}

						{!rows.isLoading && list.length === 0 && (
							<tr>
								<td colSpan={5} className="muted">
									Состав набора пуст.
									{rights.toolsetControl &&
										" Добавьте товары по артикулу или названию выше."}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}
